import { amplifyProxy } from '@/services/directApi'

export default {
  namespaced: true,
  state: {
    propertiesManagedRecords: [],
    propertiesWithoutOwnerRecords: [],
    propertiesRentCollectedRecords: [],
    propertiesWithoutTenantsRecords: [],
    propertiesNotManagedRecords: [],
    propertiesNewTenanciesRecords: [],
    propertiesHistoricRecords: [],
    totalCount: {
      managed: 0,
      ownerless: 0,
      rentCollected: 0,
      tenantless: 0,
      notManaged: 0,
      newTenancies: 0,
      historic: 0
    },
    pagination: {
      managed: { offset: 0, limit: 10 },
      ownerless: { offset: 0, limit: 10 },
      rentCollected: { offset: 0, limit: 10 },
      tenantless: { offset: 0, limit: 10 },
      notManaged: { offset: 0, limit: 10 },
      newTenancies: { offset: 0, limit: 10 },
      historic: { offset: 0, limit: 10 }
    },
    loading: {
      managed: false,
      ownerless: false,
      rentCollected: false,
      tenantless: false,
      notManaged: false,
      newTenancies: false,
      historic: false
    },
    error: {
      managed: null,
      ownerless: null,
      rentCollected: null,
      tenantless: null,
      notManaged: null,
      newTenancies: null,
      historic: null
    },
    // Page cache to store previously fetched pages
    pageCache: {
      managed: {},
      ownerless: {},
      rentCollected: {},
      tenantless: {},
      notManaged: {},
      newTenancies: {},
      historic: {}
    }
  },
  mutations: {
    SET_PROPERTIES_MANAGED(state, data) {
      state.propertiesManagedRecords = data
    },
    SET_PROPERTIES_OWNERLESS(state, data) {
      state.propertiesWithoutOwnerRecords = data
    },
    SET_PROPERTIES_RENT_COLLECTED(state, data) {
      state.propertiesRentCollectedRecords = data
    },
    SET_PROPERTIES_WITHOUT_TENANTS(state, data) {
      state.propertiesWithoutTenantsRecords = data
    },
    SET_PROPERTIES_NOT_MANAGED(state, data) {
      state.propertiesNotManagedRecords = data
    },
    SET_PROPERTIES_NEW_TENANCIES(state, data) {
      state.propertiesNewTenanciesRecords = data
    },
    SET_PROPERTIES_HISTORIC_TENANCIES(state, data) {
      state.propertiesHistoricRecords = data
    },
    SET_LOADING(state, { type, value }) {
      state.loading[type] = value
    },
    SET_ERROR(state, { type, error }) {
      state.error[type] = error
    },
    SET_TOTAL_COUNT(state, { type, count }) {
      state.totalCount[type] = count
    },
    SET_PAGINATION(state, { type, pagination }) {
      state.pagination[type] = pagination
    },
    SET_PAGE_CACHE(state, { type, key, data }) {
      state.pageCache[type][key] = data
    },
    CLEAR_PAGE_CACHE(state, type = null) {
      if (type) {
        state.pageCache[type] = {}
      } else {
        state.pageCache = {
          managed: {},
          ownerless: {},
          rentCollected: {},
          tenantless: {},
          notManaged: {},
          newTenancies: {},
          historic: {}
        }
      }
    }
  },
  actions: {
    async getPropertiesManagedRecords({ commit, state, rootState }, { offset = 0, limit = 10, forceRefresh = false, propertyId = '' } = {}) {
      const cacheKey = `${offset}_${limit}_${propertyId}`
      
      // Check cache first unless force refresh is requested
      if (!forceRefresh && state.pageCache.managed[cacheKey]) {
        const cachedData = state.pageCache.managed[cacheKey]
        commit('SET_PROPERTIES_MANAGED', cachedData.Data)
        commit('SET_TOTAL_COUNT', { type: 'managed', count: cachedData.Count })
        commit('SET_PAGINATION', { type: 'managed', pagination: { offset, limit } })
        return cachedData
      }
      
      commit('SET_LOADING', { type: 'managed', value: true })
      commit('SET_ERROR', { type: 'managed', error: null })
      
      try {
        const endpoint = `/${rootState.user.ClientName}/properties/managed/records/${offset}/${limit}`
        const options = {
          headers: {
            token: rootState.user.LoginToken,

          },
          queryStringParameters: {
            branchID: rootState.branchID,
            ...(propertyId && { propertyId })
          }
        }

        const response = await amplifyProxy.get(endpoint, options)
        
        // Cache the response
        commit('SET_PAGE_CACHE', { 
          type: 'managed', 
          key: cacheKey, 
          data: response.data 
        })
        
        const data = response.data.Data || []
        const count = response.data.Count || 0
        
        commit('SET_PROPERTIES_MANAGED', data)
        commit('SET_TOTAL_COUNT', { type: 'managed', count })
        commit('SET_PAGINATION', { type: 'managed', pagination: { offset, limit } })
        
        return response.data
      } catch (error) {
        commit('SET_ERROR', { type: 'managed', error: error.message })
        throw error
      } finally {
        commit('SET_LOADING', { type: 'managed', value: false })
      }
    },

    async getPropertiesOwnerlessRecords({ commit, state, rootState }, { offset = 0, limit = 10, forceRefresh = false, propertyId = '' } = {}) {
      const cacheKey = `${offset}_${limit}_${propertyId}`
      
      // Check cache first unless force refresh is requested
      if (!forceRefresh && state.pageCache.ownerless[cacheKey]) {
        const cachedData = state.pageCache.ownerless[cacheKey]
        commit('SET_PROPERTIES_OWNERLESS', cachedData.Data)
        commit('SET_TOTAL_COUNT', { type: 'ownerless', count: cachedData.Count })
        commit('SET_PAGINATION', { type: 'ownerless', pagination: { offset, limit } })
        return cachedData
      }
      
      commit('SET_LOADING', { type: 'ownerless', value: true })
      commit('SET_ERROR', { type: 'ownerless', error: null })
      
      try {
        const endpoint = `/${rootState.user.ClientName}/properties/ownerless/records/${offset}/${limit}`
        const options = {
          headers: {
            token: rootState.user.LoginToken,

          },
          queryStringParameters: {
            branchID: rootState.branchID,
            ...(propertyId && { propertyId })
          }
        }

        const response = await amplifyProxy.get(endpoint, options)
        
        // Cache the response
        commit('SET_PAGE_CACHE', { 
          type: 'ownerless', 
          key: cacheKey, 
          data: response.data 
        })
        
        const data = response.data.Data || []
        const count = response.data.Count || 0
        
        commit('SET_PROPERTIES_OWNERLESS', data)
        commit('SET_TOTAL_COUNT', { type: 'ownerless', count })
        commit('SET_PAGINATION', { type: 'ownerless', pagination: { offset, limit } })
        
        return response.data
      } catch (error) {
        commit('SET_ERROR', { type: 'ownerless', error: error.message })
        throw error
      } finally {
        commit('SET_LOADING', { type: 'ownerless', value: false })
      }
    },

    async getPropertiesRentCollectedRecords({ commit, state, rootState }, { offset = 0, limit = 10, forceRefresh = false } = {}) {
      const cacheKey = `${offset}_${limit}`
      
      // Check cache first unless force refresh is requested
      if (!forceRefresh && state.pageCache.rentCollected[cacheKey]) {
        const cachedData = state.pageCache.rentCollected[cacheKey]
        commit('SET_PROPERTIES_RENT_COLLECTED', cachedData.Data)
        commit('SET_TOTAL_COUNT', { type: 'rentCollected', count: cachedData.Count })
        commit('SET_PAGINATION', { type: 'rentCollected', pagination: { offset, limit } })
        return cachedData
      }
      
      commit('SET_LOADING', { type: 'rentCollected', value: true })
      commit('SET_ERROR', { type: 'rentCollected', error: null })
      
      try {
        const endpoint = `/${rootState.user.ClientName}/properties/rentcollect/records/${offset}/${limit}`
        const options = {
          headers: {
            token: rootState.user.LoginToken,

          },
          queryStringParameters: {
            branchID: rootState.branchID,
            ...(propertyId && { propertyId })
          }
        }

        const response = await amplifyProxy.get(endpoint, options)
        
        // Cache the response
        commit('SET_PAGE_CACHE', { 
          type: 'rentCollected', 
          key: cacheKey, 
          data: response.data 
        })
        
        const data = response.data.Data || []
        const count = response.data.Count || 0
        
        commit('SET_PROPERTIES_RENT_COLLECTED', data)
        commit('SET_TOTAL_COUNT', { type: 'rentCollected', count })
        commit('SET_PAGINATION', { type: 'rentCollected', pagination: { offset, limit } })
        
        return response.data
      } catch (error) {
        commit('SET_ERROR', { type: 'rentCollected', error: error.message })
        throw error
      } finally {
        commit('SET_LOADING', { type: 'rentCollected', value: false })
      }
    },

    async getPropertiesWithoutTenantsRecords({ commit, state, rootState }, { offset = 0, limit = 10, forceRefresh = false } = {}) {
      const cacheKey = `${offset}_${limit}`
      
      // Check cache first unless force refresh is requested
      if (!forceRefresh && state.pageCache.tenantless[cacheKey]) {
        const cachedData = state.pageCache.tenantless[cacheKey]
        commit('SET_PROPERTIES_WITHOUT_TENANTS', cachedData.Data)
        commit('SET_TOTAL_COUNT', { type: 'tenantless', count: cachedData.Count })
        commit('SET_PAGINATION', { type: 'tenantless', pagination: { offset, limit } })
        return cachedData
      }
      
      commit('SET_LOADING', { type: 'tenantless', value: true })
      commit('SET_ERROR', { type: 'tenantless', error: null })
      
      try {
        const endpoint = `/${rootState.user.ClientName}/properties/tenantless/records/${offset}/${limit}`
        const options = {
          headers: {
            token: rootState.user.LoginToken,

          },
          queryStringParameters: {
            branchID: rootState.branchID,
            ...(propertyId && { propertyId })
          }
        }

        const response = await amplifyProxy.get(endpoint, options)
        
        // Cache the response
        commit('SET_PAGE_CACHE', { 
          type: 'tenantless', 
          key: cacheKey, 
          data: response.data 
        })
        
        const data = response.data.Data || []
        const count = response.data.Count || 0
        
        commit('SET_PROPERTIES_WITHOUT_TENANTS', data)
        commit('SET_TOTAL_COUNT', { type: 'tenantless', count })
        commit('SET_PAGINATION', { type: 'tenantless', pagination: { offset, limit } })
        
        return response.data
      } catch (error) {
        commit('SET_ERROR', { type: 'tenantless', error: error.message })
        throw error
      } finally {
        commit('SET_LOADING', { type: 'tenantless', value: false })
      }
    },

    async getPropertiesNotManagedRecords({ commit, state, rootState }, { offset = 0, limit = 10, forceRefresh = false } = {}) {
      const cacheKey = `${offset}_${limit}`
      
      // Check cache first unless force refresh is requested
      if (!forceRefresh && state.pageCache.notManaged[cacheKey]) {
        const cachedData = state.pageCache.notManaged[cacheKey]
        commit('SET_PROPERTIES_NOT_MANAGED', cachedData.Data)
        commit('SET_TOTAL_COUNT', { type: 'notManaged', count: cachedData.Count })
        commit('SET_PAGINATION', { type: 'notManaged', pagination: { offset, limit } })
        return cachedData
      }
      
      commit('SET_LOADING', { type: 'notManaged', value: true })
      commit('SET_ERROR', { type: 'notManaged', error: null })
      
      try {
        const endpoint = `/${rootState.user.ClientName}/properties/unmanaged/records/${offset}/${limit}`
        const options = {
          headers: {
            token: rootState.user.LoginToken,

          },
          queryStringParameters: {
            branchID: rootState.branchID,
            ...(propertyId && { propertyId })
          }
        }

        const response = await amplifyProxy.get(endpoint, options)
        
        // Cache the response
        commit('SET_PAGE_CACHE', { 
          type: 'notManaged', 
          key: cacheKey, 
          data: response.data 
        })
        
        const data = response.data.Data || []
        const count = response.data.Count || 0
        
        commit('SET_PROPERTIES_NOT_MANAGED', data)
        commit('SET_TOTAL_COUNT', { type: 'notManaged', count })
        commit('SET_PAGINATION', { type: 'notManaged', pagination: { offset, limit } })
        
        return response.data
      } catch (error) {
        commit('SET_ERROR', { type: 'notManaged', error: error.message })
        throw error
      } finally {
        commit('SET_LOADING', { type: 'notManaged', value: false })
      }
    },

    async getPropertiesNewTenanciesRecords({ commit, state, rootState }, { offset = 0, limit = 10, forceRefresh = false, propertyId = '' } = {}) {
      const cacheKey = `${offset}_${limit}_${propertyId}`
      
      // Check cache first unless force refresh is requested
      if (!forceRefresh && state.pageCache.newTenancies[cacheKey]) {
        const cachedData = state.pageCache.newTenancies[cacheKey]
        commit('SET_PROPERTIES_NEW_TENANCIES', cachedData.Data)
        commit('SET_TOTAL_COUNT', { type: 'newTenancies', count: cachedData.Count })
        commit('SET_PAGINATION', { type: 'newTenancies', pagination: { offset, limit } })
        return cachedData
      }
      
      commit('SET_LOADING', { type: 'newTenancies', value: true })
      commit('SET_ERROR', { type: 'newTenancies', error: null })
      
      try {
        const endpoint = `/${rootState.user.ClientName}/properties/newtenancies/records/${offset}/${limit}`
        const options = {
          headers: {
            token: rootState.user.LoginToken,

          },
          queryStringParameters: {
            branchID: rootState.branchID,
            ...(propertyId && { propertyId })
          }
        }

        const response = await amplifyProxy.get(endpoint, options)
        
        // Cache the response
        commit('SET_PAGE_CACHE', { 
          type: 'newTenancies', 
          key: cacheKey, 
          data: response.data 
        })
        
        const data = response.data.Data || []
        const count = response.data.Count || 0
        
        commit('SET_PROPERTIES_NEW_TENANCIES', data)
        commit('SET_TOTAL_COUNT', { type: 'newTenancies', count })
        commit('SET_PAGINATION', { type: 'newTenancies', pagination: { offset, limit } })
        
        return response.data
      } catch (error) {
        commit('SET_ERROR', { type: 'newTenancies', error: error.message })
        throw error
      } finally {
        commit('SET_LOADING', { type: 'newTenancies', value: false })
      }
    },

    async getPropertiesHistoricTenanciesRecords({ commit, state, rootState }, { offset = 0, limit = 10, forceRefresh = false, propertyId = '' } = {}) {
      const cacheKey = `${offset}_${limit}_${propertyId}`
      
      // Check cache first unless force refresh is requested
      if (!forceRefresh && state.pageCache.historic[cacheKey]) {
        const cachedData = state.pageCache.historic[cacheKey]
        commit('SET_PROPERTIES_HISTORIC_TENANCIES', cachedData.Data)
        commit('SET_TOTAL_COUNT', { type: 'historic', count: cachedData.Count })
        commit('SET_PAGINATION', { type: 'historic', pagination: { offset, limit } })
        return cachedData
      }
      
      commit('SET_LOADING', { type: 'historic', value: true })
      commit('SET_ERROR', { type: 'historic', error: null })
      
      try {
        const endpoint = `/${rootState.user.ClientName}/properties/historictenancies/records/${offset}/${limit}`
        const options = {
          headers: {
            token: rootState.user.LoginToken,

          },
          queryStringParameters: {
            branchID: rootState.branchID,
            ...(propertyId && { propertyId })
          }
        }

        const response = await amplifyProxy.get(endpoint, options)
        
        // Cache the response
        commit('SET_PAGE_CACHE', { 
          type: 'historic', 
          key: cacheKey, 
          data: response.data 
        })
        
        const data = response.data.Data || []
        const count = response.data.Count || 0
        
        commit('SET_PROPERTIES_HISTORIC_TENANCIES', data)
        commit('SET_TOTAL_COUNT', { type: 'historic', count })
        commit('SET_PAGINATION', { type: 'historic', pagination: { offset, limit } })
        
        return response.data
      } catch (error) {
        commit('SET_ERROR', { type: 'historic', error: error.message })
        throw error
      } finally {
        commit('SET_LOADING', { type: 'historic', value: false })
      }
    }
  }
}